import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-or-later
import { createContext, useContext, useId } from 'react';
import { Info } from 'lucide-react';
import { ExtensionUrl } from '@intakeform/fhir';
import { FormError } from '../primitives/FormError.js';
import { Label } from '../primitives/Label.js';
import { Stack } from '../primitives/Stack.js';
import { useLanguage } from '../i18n/LanguageProvider.js';
import { translateField } from '../i18n/translate.js';
/**
 * Context for the renderer-supplied "touched" callback. Field components
 * never set this — only {@link Renderer} provides it. FieldHost reads
 * it so individual field components don't have to thread the callback
 * through their own props.
 */
const TouchedContext = createContext(undefined);
export const TouchedProvider = TouchedContext.Provider;
/**
 * Reads the inline "why we ask" help text from a Questionnaire item.
 * Stored in the `helpText` custom extension as `valueString`. Exposed
 * so callers (e.g. tests, alternative renderers) can rely on the same
 * resolution rule the host uses.
 */
export function getHelpText(item) {
    const ext = item.extension?.find((e) => e.url === ExtensionUrl.helpText);
    const value = ext?.valueString;
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}
/**
 * Joins the error and help ids into a single `aria-describedby` value.
 * Skips ids that don't apply (`errorId` only when `hasError`, `helpId`
 * only when help text is present). Returns `undefined` when nothing
 * applies so React drops the attribute entirely.
 */
export function describedBy(ctx, extra) {
    const ids = [
        ctx.hasError ? ctx.errorId : undefined,
        ctx.helpId,
        extra !== undefined && extra.length > 0 ? extra : undefined,
    ].filter((id) => typeof id === 'string' && id.length > 0);
    return ids.length === 0 ? undefined : ids.join(' ');
}
/**
 * Wrapper that owns the label, optional inline help, and error rendering
 * for a single field. Field components render only the input control;
 * everything around it (label, help, required marker, errors) is
 * consistent because it comes from this one host.
 */
export function FieldHost({ item, errors, controlId, onTouched, children }) {
    const errorId = useId();
    const helpId = useId();
    const { lang, t } = useLanguage();
    const helpText = getHelpText(item);
    const translatedHelp = helpText !== undefined ? translateField(item, 'helpText', lang, helpText) : undefined;
    const translatedLabel = item.text !== undefined ? translateField(item, 'text', lang, item.text) : undefined;
    const hasError = errors.some((e) => e.severity === 'error');
    const ctxTouched = useContext(TouchedContext);
    const effectiveTouched = onTouched ?? ctxTouched;
    const onBlur = effectiveTouched !== undefined
        ? () => {
            effectiveTouched(item.linkId);
        }
        : undefined;
    return (_jsxs(Stack, { "data-field-host": "true", "data-link-id": item.linkId, gap: 4, onBlur: onBlur, children: [translatedLabel !== undefined ? (_jsx(Label, { htmlFor: controlId, required: item.required === true, children: translatedLabel })) : null, translatedHelp !== undefined ? (_jsxs("p", { id: helpId, className: "field-help", "data-field-help": "true", "data-link-id": item.linkId, children: [_jsx("span", { className: "field-help-icon", "aria-hidden": "true", children: _jsx(Info, { size: 14, strokeWidth: 1.6 }) }), _jsxs("span", { children: [_jsx("strong", { children: t('fields.help-prefix') }), translatedHelp] })] })) : null, typeof children === 'function'
                ? children({ errorId, helpId: translatedHelp !== undefined ? helpId : undefined, hasError })
                : children, _jsx(FormError, { findings: errors, id: errorId })] }));
}
//# sourceMappingURL=FieldHost.js.map